import type { multiCartDeleteItemMachine } from 'components/Pages/UserCartPage/SharedCarts/multiCartDeleteItemMachine'
import { CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE } from 'constants/checkout'
import { CHARTER } from 'constants/provider'
import { CART_AUTO_DELETE_DAYS_AFTER_ADDED, CART_AUTO_DELETE_DAYS_AFTER_EXPIRY, NON_DUPLICABLE_ITEM_TYPES } from 'constants/userCart'
import { isAccommodationItem, isAccommodationV2Item, isBedbankItem, isBundledExperienceItem, isCruiseItem, isExperienceItem, isFlightItem, isInstantBookingLEHotelItem, isLEHotelItem, isLuxPlusSubscriptionItem, isMerchandiseItem, isSubscriptionJoinItem, isTourV2Item, isTransferItem, isVillaItem } from 'lib/checkout/checkoutUtils'
import { isMemberOnlyOffer } from 'luxPlus/selectors/cartEligibility'
import { getTourV2OfferCacheKey } from 'lib/tours/tourV2OfferCacheKey'
import { arrayToObject, unique, without } from 'lib/array/arrayUtils'
import { getAccommodationPropertyTimeZone } from 'lib/datetime/accommodationPropertyTimeZone'
import { getLocalDate } from 'lib/datetime/calendarUtils'
import { addDays, getCalendarDateStringInTimeZone, isValidDate } from 'lib/datetime/dateUtils'
import getObjectKey from 'lib/object/getObjectKey'
import { objectEntries, omitKeys } from 'lib/object/objectUtils'
import type { MachineContext } from 'typings/app/machineContext'
import type { ActorRefFrom, AnyActorRef, SnapshotFrom } from 'xstate'
import type multiCartMachine from './multiCartMachine'

export type MultiCartMachineChildren = SnapshotFrom<typeof multiCartMachine>['children']
type MultiCartDeleteItemActor = ActorRefFrom<typeof multiCartDeleteItemMachine>
type MultiCartDeleteItemSnapshot = SnapshotFrom<typeof multiCartDeleteItemMachine>

// Helper to get cart display name
export const getCartDisplayName = (context: MachineContext.MultiCartMachineContext, cartId?: string): string => {
  const id = cartId ?? context.activeCartId
  if (!id || context.cartSummary.status !== 'loaded') return 'cart'

  const cart = context.cartSummary.data[id]
  return cart?.name === 'default' ? 'cart' : cart?.name || 'cart'
}

export const updateCartItemsInCart = (
  context: MachineContext.MultiCartMachineContext,
  cartId: string,
  updater: (items: Record<string, App.MultiCartItem>) => Record<string, App.MultiCartItem>,
) => {
  if (context.cartItems.status !== 'loaded') {
    return context.cartItems
  }

  const updatedCartItems = { ...context.cartItems.data }

  if (updatedCartItems[cartId]) {
    updatedCartItems[cartId] = updater({ ...updatedCartItems[cartId] })
  }

  return {
    status: 'loaded' as const,
    data: updatedCartItems,
  }
}

function filterNonDuplicableItems(itemsToAdd: Array<App.Checkout.AnyItem>, existingItems: MachineContext.CartItems['any']) {
  // For item types that can only have one item in the cart, filter out new items if that type already exists
  const existingItemTypes = new Set(Object.values(existingItems).map(item => item.item.itemType))
  const itemTypesToFilter = new Set<App.Checkout.AnyItem['itemType']>(
    NON_DUPLICABLE_ITEM_TYPES.filter(type => existingItemTypes.has(type)),
  )
  return itemsToAdd.filter(item => !itemTypesToFilter.has(item.itemType))
}

function getStableOccupancy(occupancy: App.Occupants | App.RoomOccupants) {
  return {
    adults: occupancy.adults,
    children: occupancy.children,
    childrenAge: occupancy.childrenAge,
    infants: occupancy.infants,
  }
}

function getAbandonItemBedbankKey(item: App.Checkout.BedbankHotelItem) {
  const { offerId, boardCode, roomId, refundable, occupancy, checkIn, checkOut } = item
  return getObjectKey({
    offerId,
    boardCode,
    roomId,
    refundable,
    stableOccupancy: getStableOccupancy(occupancy),
    checkIn,
    checkOut,
  })
}

function getAbandonItemHotelKey(item: App.Checkout.InstantBookingLEHotelItem) {
  const { offerId, packageId, occupancy, checkIn, checkOut } = item
  return getObjectKey({
    offerId,
    packageId,
    stableOccupancy: getStableOccupancy(occupancy),
    checkIn,
    checkOut,
  })
}

function getAbandonItemFlightKey(item: App.Checkout.FlightItem) {
  const { occupants, travelStart, travelEnd, flights } = item
  return getObjectKey({
    occupants,
    travelStart,
    travelEnd,
    flights: flights.map(flight => ({ journeyKey: flight.journeyKey, fareFamily: flight.fareFamily?.fareFamily })),
  })
}

function getAbandonItemKey(item: App.Checkout.AnyItem): string | undefined {
  if (isBedbankItem(item)) return getAbandonItemBedbankKey(item)
  if (isInstantBookingLEHotelItem(item)) return getAbandonItemHotelKey(item)
  if (isFlightItem(item)) return getAbandonItemFlightKey(item)
}

function filterAlreadyAbandonedItems(itemsToAdd: Array<App.Checkout.AnyItem>, existingItems: MachineContext.CartItems['any']) {
  const existingItemKeys = new Set(
    Object.values(existingItems)
      .filter(item => item.state !== 'deleted')
      .map(item => getAbandonItemKey(item.item))
      .filter((key): key is string => !!key),
  )

  if (!existingItemKeys.size) return itemsToAdd

  return itemsToAdd.filter(item => {
    const key = getAbandonItemKey(item)
    return !key || !existingItemKeys.has(key)
  })
}

function getMatchingCartEntryId(entries: Array<App.MultiCartItem>, item: App.Checkout.AnyItem): string | undefined {
  const exactEntry = entries.find(entry => entry.id === item.itemId)
  if (exactEntry) return exactEntry.id
  const originalMatches = entries.filter(entry => entry.item.itemId === item.itemId)
  if (originalMatches.length === 1) return originalMatches[0].id
  if (!originalMatches.length || !item.transactionKey) return
  const transactionMatches = entries.filter(entry => entry.item.transactionKey === item.transactionKey)
  if (transactionMatches.length === 1 && transactionMatches[0].item.itemId === item.itemId) {
    return transactionMatches[0].id
  }
}

export function getCartItemsToPromote(context: MachineContext.MultiCartMachineContext, items: Array<App.Checkout.AnyItem>, cartId?: string): Array<string> {
  if (!cartId || context.cartItems.status !== 'loaded') return []
  const existingEntries = Object.values(context.cartItems.data[cartId] ?? {}).filter(entry => entry.state !== 'deleted')
  const matchingIds = new Set(items.map(item => getMatchingCartEntryId(existingEntries, item)))
  const nonDuplicableTypes = new Set<App.Checkout.AnyItem['itemType']>(NON_DUPLICABLE_ITEM_TYPES.filter(type => items.some(item => item.itemType === type)))
  return existingEntries
    .filter(entry => entry.intent !== 'deliberate' && (matchingIds.has(entry.id) || nonDuplicableTypes.has(entry.item.itemType)))
    .map(entry => entry.id)
}

// New utility function for getting deduped items to add to cart
export function getDedupedItemsForCart(
  context: MachineContext.MultiCartMachineContext,
  items: Array<App.Checkout.AnyItem>,
  dedupeAbandonedItems: boolean = false,
  cartId: string | undefined = context.activeCartId,
  excludeExactMatches: boolean = false,
): Array<App.Checkout.AnyItem> {
  const existingCartItemsByCart = context.cartItems.status === 'loaded' ? context.cartItems.data : {}
  const existingCartItems = cartId ? existingCartItemsByCart[cartId] ?? {} : {}
  const existingEntries = Object.values(existingCartItems).filter(entry => entry.state !== 'deleted')
  const itemsWithoutExactMatches = excludeExactMatches ? items.filter(item => !getMatchingCartEntryId(existingEntries, item)) : items
  const duplicableItems = filterNonDuplicableItems(itemsWithoutExactMatches, existingCartItems)
  return dedupeAbandonedItems
    ? filterAlreadyAbandonedItems(duplicableItems, existingCartItems)
    : duplicableItems
}

// Utility to check if items can be added to active cart
export function getHasItemsToAdd(
  context: MachineContext.MultiCartMachineContext,
  items: Array<App.Checkout.AnyItem>,
  dedupeAbandonedItems: boolean = false,
  cartId: string | undefined = context.activeCartId,
): boolean {
  // Check if there's an active cart
  if (!cartId) {
    return false
  }

  // Check if cart items are loaded
  if (context.cartItems.status !== 'loaded') {
    return false
  }

  // Check if there are remaining items to add after deduplication
  const dedupedItems = getDedupedItemsForCart(context, items, dedupeAbandonedItems, cartId)
  return !!dedupedItems.length
}

export function expandWithBundledChildren(
  items: Record<string, App.MultiCartItem>,
  itemIds: Array<string>,
  claimedItemIds: Set<string> = new Set(),
): Array<string> {
  const expandedIds = new Set(itemIds)
  const parentInnerItemIds = new Set(
    itemIds
      .filter((itemId) => items[itemId]?.item?.itemId && isAccommodationItem(items[itemId].item))
      .map((itemId) => items[itemId].item.itemId),
  )

  if (parentInnerItemIds.size === 0) return Array.from(expandedIds)

  for (const candidate of Object.values(items)) {
    if (candidate.state === 'deleted') continue

    if (isBundledExperienceItem(candidate.item)) {
      const experienceOrTransfer = candidate.item
      const isParentAccomBundle = !!experienceOrTransfer.isAccomBundle && !!experienceOrTransfer.bundledItemIds?.some(parentInnerItemId => parentInnerItemIds.has(parentInnerItemId))
      const isParentPassport = isExperienceItem(experienceOrTransfer) && !!experienceOrTransfer.accommodationParentItemId && parentInnerItemIds.has(experienceOrTransfer.accommodationParentItemId)

      if ((isParentAccomBundle || isParentPassport) && !claimedItemIds.has(candidate.id)) {
        expandedIds.add(candidate.id)
      }
    }
  }
  return Array.from(expandedIds)
}

/**
 * Stable actor id for multi-cart soft-delete/undo.
 * Delete and undo UIs can pass the same item set in different orders
 * (e.g. hotel-first from LeHotelTile vs date-sorted FlightHotelBundle undo),
 * so the id must not depend on array order.
 */
export const MULTI_CART_DELETE_ITEM_MACHINE_ID_PREFIX = 'deleteItemMachine-'

export function getMultiCartDeleteItemMachineId(itemIds: Array<string>): string {
  return `${MULTI_CART_DELETE_ITEM_MACHINE_ID_PREFIX}${[...itemIds].sort().join('-')}`
}

export function getDeleteItemMachineIdsForUndo(
  requestedItemIds: Array<string>,
  children: MultiCartMachineChildren,
): Array<string> {
  const spawnedActorIds = objectEntries(children).flatMap(([actorId]) => (
    typeof actorId === 'string' ? [actorId] : []
  ))
  const spawned = new Set(spawnedActorIds)
  const requested = new Set(requestedItemIds)

  const byActorId = unique([
    getMultiCartDeleteItemMachineId(requestedItemIds),
    ...requestedItemIds.map(itemId => getMultiCartDeleteItemMachineId([itemId])),
  ]).filter(id => spawned.has(id))

  const byContextItemIds = objectEntries(children).flatMap(([actorId, child]) => {
    if (typeof actorId !== 'string' || !isMultiCartDeleteItemActor(actorId, child)) return []

    const snapshot = child.getSnapshot()
    if (snapshot.status === 'done' || snapshot.value === 'done') return []
    if (!snapshot.context.itemIds.some(itemId => requested.has(itemId))) return []

    return [actorId]
  })

  return unique([...byActorId, ...byContextItemIds])
}

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

function parseCartAutoDeleteDate(value?: string): Date | undefined {
  if (!value) return undefined

  // Date-only bookings expire when their travel day ends.
  const date = value.includes('T') ? new Date(value) : addDays(getLocalDate(value), 1)
  return isValidDate(date) ? date : undefined
}

function getIsoDatePart(value?: string): string | undefined {
  if (!value) return undefined

  const isoDate = value.includes('T') ? value.slice(0, 10) : value
  return /^\d{4}-\d{2}-\d{2}$/.test(isoDate) ? isoDate : undefined
}

function isDateOnlyExpiredForAutoDelete(value: string | undefined, now: Date, timeZone?: string): boolean {
  if (!timeZone) return false

  const isoDate = getIsoDatePart(value)
  if (!isoDate) return false

  const beforeGracePeriod = new Date(now.getTime() - MILLISECONDS_PER_DAY * CART_AUTO_DELETE_DAYS_AFTER_EXPIRY)
  return isoDate < getCalendarDateStringInTimeZone(beforeGracePeriod, timeZone)
}

function getCheckoutItemOfferId(item: App.Checkout.AnyItem): string | undefined {
  return 'offerId' in item && typeof item.offerId === 'string' ? item.offerId : undefined
}

export function getCartItemPropertyTimeZone(
  item: App.Checkout.AnyItem,
  getOffer?: (offerId: string) => { property?: { timezone?: string } } | undefined,
): string | undefined {
  const offerId = getCheckoutItemOfferId(item)
  if (!offerId || !getOffer) return undefined

  return getAccommodationPropertyTimeZone(getOffer(offerId))
}

function isAccommodationAwaitingPropertyTimeZone(
  item: App.Checkout.AnyItem,
  getOffer?: (offerId: string) => { property?: { timezone?: string } } | undefined,
): boolean {
  if (!isLEHotelItem(item) && !isBedbankItem(item) && !isVillaItem(item) && !isAccommodationV2Item(item)) {
    return false
  }

  const checkIn = 'checkIn' in item ? item.checkIn : undefined
  if (!getIsoDatePart(checkIn)) return false

  return !getCartItemPropertyTimeZone(item, getOffer)
}

function cartHasPendingAccommodationPropertyTimeZone(
  items: Record<string, App.MultiCartItem>,
  now: Date,
  getOffer?: (offerId: string) => { property?: { timezone?: string } } | undefined,
): boolean {
  return Object.values(items).some((cartItem) => {
    if (cartItem.state !== 'active' && cartItem.state !== 'save_for_later') return false
    if (isCartItemPastAddedRetention(cartItem, now)) return false
    return isAccommodationAwaitingPropertyTimeZone(cartItem.item, getOffer)
  })
}

function getCartItemAutoDeleteDate(item: App.Checkout.AnyItem): Date | undefined {
  if (
    isMerchandiseItem(item) ||
    isLuxPlusSubscriptionItem(item) ||
    item.itemType === CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE
  ) {
    return undefined
  }

  if (isLEHotelItem(item) || isBedbankItem(item) || isVillaItem(item) || isAccommodationV2Item(item)) {
    return undefined
  }

  if (isCruiseItem(item) || isTourV2Item(item)) {
    return parseCartAutoDeleteDate(item.startDate)
  }

  if (isFlightItem(item)) {
    return parseCartAutoDeleteDate(item.flights?.[0]?.departingDate)
  }

  if (isExperienceItem(item)) {
    return parseCartAutoDeleteDate(item.date)
  }

  if (isTransferItem(item)) {
    return parseCartAutoDeleteDate(item.transfer?.date)
  }

  return undefined
}

export function isCartItemExpiredForAutoDelete(
  item: App.Checkout.AnyItem,
  now: Date,
  timeZone?: string,
): boolean {
  // Charter expiry represents a booking lead-time cutoff; keep its travel/age deadlines.
  if (isFlightItem(item) && item.expiry && item.expiry > 0 && Number.isFinite(item.expiry) &&
    !item.flights.some(flight => flight.provider === CHARTER) &&
    item.expiry + MILLISECONDS_PER_DAY * CART_AUTO_DELETE_DAYS_AFTER_EXPIRY <= now.getTime()) {
    return true
  }

  if (isLEHotelItem(item) || isBedbankItem(item) || isVillaItem(item) || isAccommodationV2Item(item)) {
    return isDateOnlyExpiredForAutoDelete('checkIn' in item ? item.checkIn : undefined, now, timeZone)
  }

  const expiryDate = getCartItemAutoDeleteDate(item)
  if (!expiryDate) return false

  return expiryDate.getTime() + MILLISECONDS_PER_DAY * CART_AUTO_DELETE_DAYS_AFTER_EXPIRY <= now.getTime()
}

function isCartItemPastAddedRetention(cartItem: App.MultiCartItem, now: Date): boolean {
  if (!cartItem.createdAt) return false

  // Includes pre-existing items on their first sweep after release; the API supplies
  // the stored creation timestamp, so edits and future travel dates do not extend retention.
  const createdAt = new Date(cartItem.createdAt)
  return isValidDate(createdAt) &&
    createdAt.getTime() + MILLISECONDS_PER_DAY * CART_AUTO_DELETE_DAYS_AFTER_ADDED <= now.getTime()
}

export function getExpiredCartAutoDeleteRootItemIds(
  items: Record<string, App.MultiCartItem>,
  now: Date,
  getOffer?: (offerId: string) => { property?: { timezone?: string } } | undefined,
): Array<string> {
  const eligibleIds = Object.values(items)
    .filter((cartItem) => {
      if (cartItem.state !== 'active' && cartItem.state !== 'save_for_later') return false
      return isCartItemPastAddedRetention(cartItem, now) || isCartItemExpiredForAutoDelete(
        cartItem.item,
        now,
        getCartItemPropertyTimeZone(cartItem.item, getOffer),
      )
    })
    .map((cartItem) => cartItem.id)

  const eligibleSet = new Set(eligibleIds)
  const coveredChildIds = new Set(
    eligibleIds.flatMap((itemId) => {
      const cartItem = items[itemId]
      if (!cartItem || !isAccommodationItem(cartItem.item)) return []

      return expandWithBundledChildren(items, [itemId])
        .filter(expandedId => expandedId !== itemId && eligibleSet.has(expandedId))
    }),
  )

  return eligibleIds.filter((itemId) => !coveredChildIds.has(itemId))
}

function canWriteLoadedCart(
  context: MachineContext.MultiCartMachineContext,
  cartId: string,
): boolean | undefined {
  if (context.cartMembers.status !== 'loaded' || !context.memberId) {
    return undefined
  }

  const members = context.cartMembers.data[cartId]
  if (!members) {
    return undefined
  }

  if (!Array.isArray(members)) {
    return undefined
  }

  if (members.length === 0) {
    return true
  }

  const currentMember = members.find((member) => member.userId === context.memberId)
  if (!currentMember) {
    return undefined
  }

  return currentMember.accessLevel === 'owner' || currentMember.accessLevel === 'editor'
}

export type ExpiredCartAutoDeleteRequest = {
  cartId: string
  itemIds: Array<string>
  cascadeItemIds: Array<string>
  currentStates: Record<string, App.MultiCartItemState>
}

export function getUnsweptExpiredCartAutoDeletes(
  context: MachineContext.MultiCartMachineContext,
  now: Date,
): {
  cartIdsToMark: Array<string>
  requests: Array<ExpiredCartAutoDeleteRequest>
} {
  if (context.cartItems.status !== 'loaded') {
    return { cartIdsToMark: [], requests: [] }
  }

  const cartItemsData = context.cartItems.data
  const writableUnsweptCartIds = objectEntries(cartItemsData)
    .filter((entry): entry is [string, Record<string, App.MultiCartItem>] => {
      const [cartId] = entry
      return typeof cartId === 'string' &&
        !context.sweptCartIds[cartId] &&
        canWriteLoadedCart(context, cartId) === true
    })
    .map(([cartId]) => cartId)
  const cartIdsToMark = writableUnsweptCartIds.filter(cartId => (
    !cartHasPendingAccommodationPropertyTimeZone(cartItemsData[cartId], now, context.getOfferById)
  ))
  const requests: Array<ExpiredCartAutoDeleteRequest> = []

  writableUnsweptCartIds.forEach((cartId) => {
    const items = cartItemsData[cartId]
    const claimedItemIds = new Set<string>()
    getExpiredCartAutoDeleteRootItemIds(items, now, context.getOfferById).forEach((itemId) => {
      const cartItem = items[itemId]
      if (!cartItem) return

      const cascadeItemIds = expandWithBundledChildren(items, [itemId], claimedItemIds)
      cascadeItemIds.forEach(id => claimedItemIds.add(id))

      requests.push({
        cartId,
        itemIds: [itemId],
        cascadeItemIds,
        currentStates: arrayToObject(
          cascadeItemIds,
          id => id,
          id => items[id]?.state ?? cartItem.state,
        ),
      })
    })
  })

  return { cartIdsToMark, requests }
}

const MEMBER_ONLY_CAPABLE_CART_ITEM_TYPES = new Set([
  'hotel',
  'bedbankHotel',
  'accommodationV2',
  'villa',
  'tourV2',
  'cruise',
  'experience',
])

export interface UnresolvedLuxPlusCartOffer {
  offerId: string
  itemType: string
  privateRequestKey?: string
}

export interface LuxPlusCartCleanupDerivationInput {
  cartItems: MachineContext.MultiCartMachineContext['cartItems']
  cartSummary: MachineContext.MultiCartMachineContext['cartSummary']
  cartMembers: MachineContext.MultiCartMachineContext['cartMembers']
  offers: Record<string, App.AnyOffer | undefined>
  memberId: string | undefined
  eventId: string | undefined
  hasEnded: boolean
  protectedItemKey: string
  generation: number
}

export function hasLuxPlusCartCleanupDerivationChanged(
  previous: LuxPlusCartCleanupDerivationInput | undefined,
  next: LuxPlusCartCleanupDerivationInput,
): boolean {
  if (!previous) return true
  return previous.cartItems !== next.cartItems ||
    previous.cartSummary !== next.cartSummary ||
    previous.cartMembers !== next.cartMembers ||
    previous.offers !== next.offers ||
    previous.memberId !== next.memberId ||
    previous.eventId !== next.eventId ||
    previous.hasEnded !== next.hasEnded ||
    previous.protectedItemKey !== next.protectedItemKey ||
    previous.generation !== next.generation
}

export interface LuxPlusMemberOnlyCartCleanupPlan {
  requests: Array<ExpiredCartAutoDeleteRequest>
  unresolvedOfferIds: Array<string>
  unresolvedOffers: Array<UnresolvedLuxPlusCartOffer>
}

function getMultiCartItemOfferId(cartItem: App.MultiCartItem): string | undefined {
  if ('offerId' in cartItem && typeof cartItem.offerId === 'string') return cartItem.offerId
  if ('offerId' in cartItem.item && typeof cartItem.item.offerId === 'string') return cartItem.item.offerId
  return undefined
}

function isLuxPlusJoinCartItem(cartItem: App.MultiCartItem): boolean {
  return isLuxPlusSubscriptionItem(cartItem.item) || isSubscriptionJoinItem(cartItem.item)
}

function getCartTourPrivateRequestKey(cartItem: App.MultiCartItem): string | undefined {
  if (cartItem.type !== 'tourV2') return undefined
  if ('privateRequestKey' in cartItem && cartItem.privateRequestKey) return cartItem.privateRequestKey
  if ('privateRequestKey' in cartItem.item && typeof cartItem.item.privateRequestKey === 'string') {
    return cartItem.item.privateRequestKey
  }
  return undefined
}

function getCartOfferLookup(cartItem: App.MultiCartItem, offerId: string): { lookupKey: string, privateRequestKey?: string } {
  const privateRequestKey = getCartTourPrivateRequestKey(cartItem)
  if (!privateRequestKey) return { lookupKey: offerId }

  return {
    lookupKey: getTourV2OfferCacheKey(offerId, privateRequestKey),
    privateRequestKey,
  }
}

function isCheckoutJoinProtectedCartItem(cartItem: App.MultiCartItem, protectedItemIds: ReadonlySet<string>): boolean {
  if (protectedItemIds.size === 0) return false
  if (protectedItemIds.has(cartItem.id)) return true
  return 'itemId' in cartItem.item && typeof cartItem.item.itemId === 'string' && protectedItemIds.has(cartItem.item.itemId)
}

const EMPTY_PROTECTED_ITEM_IDS: ReadonlySet<string> = new Set()

function getMemberOnlyCleanupRootItemIds(
  items: Record<string, App.MultiCartItem>,
  offersById: Record<string, App.AnyOffer | undefined>,
  unresolvedOffers: Map<string, UnresolvedLuxPlusCartOffer>,
  protectedItemIds: ReadonlySet<string>,
): Array<string> {
  const eligibleIds = Object.values(items)
    .filter((cartItem) => {
      if (cartItem.state !== 'active' && cartItem.state !== 'save_for_later') return false
      if (isCheckoutJoinProtectedCartItem(cartItem, protectedItemIds)) return false
      const offerId = getMultiCartItemOfferId(cartItem)
      if (!offerId || !MEMBER_ONLY_CAPABLE_CART_ITEM_TYPES.has(cartItem.type)) return false
      const lookup = getCartOfferLookup(cartItem, offerId)
      const offer = offersById[lookup.lookupKey]
      if (!offer) {
        unresolvedOffers.set(`${cartItem.type}:${lookup.lookupKey}`, {
          itemType: cartItem.type,
          offerId,
          ...(lookup.privateRequestKey ? { privateRequestKey: lookup.privateRequestKey } : {}),
        })
        return false
      }
      return isMemberOnlyOffer(offer)
    })
    .map(cartItem => cartItem.id)

  const eligibleSet = new Set(eligibleIds)
  const coveredChildIds = new Set(
    eligibleIds.flatMap((itemId) => {
      const cartItem = items[itemId]
      if (!cartItem || !isAccommodationItem(cartItem.item)) return []

      return expandWithBundledChildren(items, [itemId])
        .filter(expandedId => expandedId !== itemId && eligibleSet.has(expandedId))
    }),
  )

  return eligibleIds.filter(itemId => !coveredChildIds.has(itemId))
}

/**
 * Member-only rows on writable default carts. Shared carts are skipped.
 * A cart is exempt only while it contains an active LuxPlus join item.
 * A saved or deleted join does not keep member-only items in the cart.
 * Missing offers are reported and not treated as finished.
 */
export function getLuxPlusMemberOnlyCartCleanupPlan(
  context: MachineContext.MultiCartMachineContext,
  offersById: Record<string, App.AnyOffer | undefined>,
  protectedItemIds: ReadonlySet<string> = EMPTY_PROTECTED_ITEM_IDS,
): LuxPlusMemberOnlyCartCleanupPlan {
  if (context.cartItems.status !== 'loaded' || context.cartSummary.status !== 'loaded') {
    return { requests: [], unresolvedOfferIds: [], unresolvedOffers: [] }
  }

  const unresolvedOffers = new Map<string, UnresolvedLuxPlusCartOffer>()
  const requests: Array<ExpiredCartAutoDeleteRequest> = []

  objectEntries(context.cartItems.data).forEach((entry) => {
    const [cartId, items] = entry
    if (typeof cartId !== 'string') return
    if (context.cartSummary.status !== 'loaded' || context.cartSummary.data[cartId]?.type !== 'default') return
    if (canWriteLoadedCart(context, cartId) !== true) return
    if (Object.values(items).some(cartItem => cartItem.state === 'active' && isLuxPlusJoinCartItem(cartItem))) return

    const claimedItemIds = new Set<string>()
    getMemberOnlyCleanupRootItemIds(items, offersById, unresolvedOffers, protectedItemIds).forEach((itemId) => {
      const cartItem = items[itemId]
      if (!cartItem) return

      const cascadeItemIds = expandWithBundledChildren(items, [itemId], claimedItemIds)
      cascadeItemIds.forEach(id => claimedItemIds.add(id))
      requests.push({
        cartId,
        itemIds: [itemId],
        cascadeItemIds,
        currentStates: arrayToObject(
          cascadeItemIds,
          id => id,
          id => items[id]?.state ?? cartItem.state,
        ),
      })
    })
  })

  const sortedUnresolvedOffers = Array.from(unresolvedOffers.values()).sort((offerA, offerB) => {
    const identityA = `${offerA.itemType}:${offerA.offerId}:${offerA.privateRequestKey ?? ''}`
    const identityB = `${offerB.itemType}:${offerB.offerId}:${offerB.privateRequestKey ?? ''}`
    return identityA.localeCompare(identityB)
  })

  return {
    requests,
    unresolvedOfferIds: sortedUnresolvedOffers.map(offer => offer.privateRequestKey ? `${offer.offerId}:${offer.privateRequestKey}` : offer.offerId),
    unresolvedOffers: sortedUnresolvedOffers,
  }
}

function isMultiCartDeleteItemContext(
  context: unknown,
): context is MultiCartDeleteItemSnapshot['context'] {
  if (!context || typeof context !== 'object') return false
  if (!('cartId' in context) || !('itemIds' in context)) return false
  return typeof context.cartId === 'string' && Array.isArray(context.itemIds)
}

function isMultiCartDeleteItemActor(
  actorId: string,
  child: AnyActorRef | undefined,
): child is MultiCartDeleteItemActor {
  if (!actorId.startsWith(MULTI_CART_DELETE_ITEM_MACHINE_ID_PREFIX) || !child) {
    return false
  }

  return isMultiCartDeleteItemContext(child.getSnapshot().context)
}

export function getInFlightDeleteItemIds(
  children: MultiCartMachineChildren,
  cartId: string,
): Set<string> {
  return new Set(
    objectEntries(children).flatMap(([actorId, child]) => {
      if (typeof actorId !== 'string' || !isMultiCartDeleteItemActor(actorId, child)) return []

      const snapshot = child.getSnapshot()
      if (snapshot.status === 'done' || snapshot.value === 'done') return []
      if (snapshot.context.cartId !== cartId) return []

      return snapshot.context.itemIds
    }),
  )
}

export function getClaimedDeleteItemIds(
  claimedDeleteItemIds: Record<string, Array<string>> | undefined,
  children: MultiCartMachineChildren,
  cartId: string,
): Set<string> {
  return new Set([
    ...(claimedDeleteItemIds?.[cartId] ?? []),
    ...getInFlightDeleteItemIds(children, cartId),
  ])
}

export function getExclusiveDeleteCascadeItemIds(
  items: Record<string, App.MultiCartItem> | undefined,
  itemIds: Array<string>,
  claimedItemIds: Set<string>,
  precomputedCascadeItemIds?: Array<string>,
): Array<string> {
  if (precomputedCascadeItemIds) {
    return precomputedCascadeItemIds.filter((itemId) => itemIds.includes(itemId) || !claimedItemIds.has(itemId))
  }

  if (!items) return itemIds

  return expandWithBundledChildren(items, itemIds, claimedItemIds)
}

export function addClaimedDeleteItemIds(
  claimedDeleteItemIds: Record<string, Array<string>>,
  cartId: string,
  itemIds: Array<string>,
): Record<string, Array<string>> {
  return {
    ...claimedDeleteItemIds,
    [cartId]: unique([...(claimedDeleteItemIds[cartId] ?? []), ...itemIds]),
  }
}

export function removeClaimedDeleteItemIds(
  claimedDeleteItemIds: Record<string, Array<string>>,
  cartId: string,
  itemIds: Array<string>,
): Record<string, Array<string>> {
  const remaining = without(claimedDeleteItemIds[cartId] ?? [], ...itemIds)
  if (remaining.length === 0) {
    return omitKeys([cartId], claimedDeleteItemIds)
  }

  return {
    ...claimedDeleteItemIds,
    [cartId]: remaining,
  }
}

export function getSaveForLaterEligibleItemIds(
  items: Record<string, App.MultiCartItem> | undefined,
  requestedIds: Array<string>,
  inFlightDeleteItemIds: Set<string> = new Set(),
): Array<string> {
  if (!items) return []

  return expandWithBundledChildren(items, requestedIds).filter((itemId) => {
    const cartItem = items[itemId]
    if (!cartItem || cartItem.state === 'deleted') return false
    return !inFlightDeleteItemIds.has(itemId)
  })
}
